import React from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TextInput,
    TouchableOpacity,
    Dimensions,
    ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width } = Dimensions.get('window');

// Detect if we're in development (Expo dev server) or production
const isDev = typeof window !== 'undefined' && window.location && (window.location.port === '19006' || window.location.port === '8081');
const API_BASE = isDev ? 'http://localhost:4000' : '';

const ITRFilingScreen = () => {
    const { colors } = useTheme();
    const isMobile = width < 768;
    const isSmallMobile = width < 400;

    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        phone: '',
        itrType: '',
        incomeSource: '',
        message: '',
    });
    const [loading, setLoading] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [error, setError] = React.useState('');

    const itrTypes = [
        { id: 'itr-1', label: 'ITR-1 (Sahaj)' },
        { id: 'itr-2', label: 'ITR-2' },
        { id: 'itr-3', label: 'ITR-3' },
        { id: 'itr-4', label: 'ITR-4 (Sugam)' },
        { id: 'not-sure', label: 'Not Sure' },
    ];

    const features = [
        { icon: 'calculator', title: 'Tax Optimization', desc: 'Maximize deductions legally' },
        { icon: 'document-attach', title: 'AIS Integration', desc: 'Auto-fetch tax data' },
        { icon: 'shield-checkmark', title: 'Error-Free Filing', desc: 'Verified before submission' },
        { icon: 'flash', title: 'Quick Refunds', desc: 'Faster refund processing' },
    ];

    const handleSubmit = async () => {
        if (!formData.name || !formData.email || !formData.phone) {
            setError('Please fill in all required fields');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE}/api/service-quote`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    service: 'itr-filing',
                    businessType: formData.itrType,
                    message: `Income Source: ${formData.incomeSource}\n${formData.message}`,
                }),
            });

            const data = await response.json();
            if (data.ok) {
                setSubmitted(true);
            } else {
                setError(data.error || 'Something went wrong. Please try again.');
            }
        } catch (e) {
            setError('Unable to submit request. Please try again or contact us directly.');
        } finally {
            setLoading(false);
        }
    };

    const styles = StyleSheet.create({
        container: { flex: 1, backgroundColor: colors.background },
        scrollView: { flex: 1 },
        hero: {
            backgroundColor: '#1a1a4e',
            paddingVertical: isMobile ? 40 : 60,
            paddingHorizontal: isSmallMobile ? 12 : 20,
        },
        heroContent: {
            maxWidth: 1100,
            alignSelf: 'center',
            width: '100%',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: 40,
        },
        heroLeft: { flex: 1 },
        heroLabel: {
            fontSize: 13,
            color: '#c9a44e',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: 1,
            marginBottom: 10,
        },
        heroTitle: {
            fontSize: isMobile ? 32 : 44,
            fontWeight: '800',
            color: '#fff',
            marginBottom: 16,
            lineHeight: isMobile ? 40 : 52,
        },
        heroSubtitle: {
            fontSize: 16,
            color: 'rgba(255,255,255,0.8)',
            lineHeight: 26,
            marginBottom: 24,
        },
        heroRight: {
            flex: isMobile ? undefined : 1,
            width: isMobile ? '100%' : undefined,
        },
        formCard: {
            backgroundColor: '#fff',
            borderRadius: isSmallMobile ? 12 : 16,
            padding: isSmallMobile ? 16 : 28,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.15,
            shadowRadius: 24,
            elevation: 12,
        },
        formTitle: {
            fontSize: isSmallMobile ? 18 : 22,
            fontWeight: '700',
            color: '#1a1a4e',
            marginBottom: 6,
        },
        formSubtitle: {
            fontSize: isSmallMobile ? 12 : 14,
            color: '#6b7280',
            marginBottom: isSmallMobile ? 12 : 20,
        },
        inputGroup: { marginBottom: 16 },
        label: {
            fontSize: 14,
            fontWeight: '600',
            color: '#374151',
            marginBottom: 8,
        },
        required: { color: '#dc2626' },
        input: {
            backgroundColor: '#f8fafc',
            borderWidth: 1,
            borderColor: '#e5e7eb',
            borderRadius: 8,
            paddingHorizontal: 14,
            paddingVertical: 12,
            fontSize: 15,
            color: '#1f2937',
        },
        typeGrid: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 10,
        },
        typeButton: {
            paddingVertical: 10,
            paddingHorizontal: 14,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#e5e7eb',
            backgroundColor: '#f8fafc',
        },
        typeButtonSelected: {
            backgroundColor: '#1a1a4e',
            borderColor: '#1a1a4e',
        },
        typeButtonText: {
            fontSize: 13,
            color: '#374151',
        },
        typeButtonTextSelected: {
            color: '#c9a44e',
        },
        textArea: {
            height: 80,
            textAlignVertical: 'top',
        },
        submitButton: {
            backgroundColor: '#c9a44e',
            paddingVertical: 16,
            borderRadius: 8,
            alignItems: 'center',
            marginTop: 8,
        },
        submitButtonText: {
            color: '#1a1a4e',
            fontWeight: '700',
            fontSize: 16,
        },
        errorText: {
            color: '#dc2626',
            fontSize: 14,
            marginBottom: 12,
            textAlign: 'center',
        },
        successCard: {
            backgroundColor: '#fff',
            borderRadius: 16,
            padding: 40,
            alignItems: 'center',
        },
        successIcon: {
            width: 80,
            height: 80,
            borderRadius: 40,
            backgroundColor: '#10b981',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 20,
        },
        successTitle: {
            fontSize: 24,
            fontWeight: '700',
            color: '#1a1a4e',
            marginBottom: 12,
            textAlign: 'center',
        },
        successText: {
            fontSize: 15,
            color: '#6b7280',
            textAlign: 'center',
            lineHeight: 24,
        },
        featuresSection: {
            paddingVertical: 60,
            paddingHorizontal: 20,
            backgroundColor: '#f8fafc',
        },
        featuresContainer: {
            maxWidth: 1100,
            alignSelf: 'center',
            width: '100%',
        },
        sectionTitle: {
            fontSize: isMobile ? 28 : 36,
            fontWeight: '800',
            color: '#1a1a4e',
            textAlign: 'center',
            marginBottom: 40,
        },
        featuresGrid: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 24,
        },
        featureCard: {
            backgroundColor: '#fff',
            borderRadius: 12,
            padding: 24,
            width: isMobile ? '100%' : '22%',
            minWidth: 220,
            alignItems: 'center',
            borderWidth: 1,
            borderColor: '#e5e7eb',
        },
        featureIcon: {
            width: 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: '#1a1a4e',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 16,
        },
        featureTitle: {
            fontSize: 16,
            fontWeight: '700',
            color: '#1a1a4e',
            marginBottom: 8,
            textAlign: 'center',
        },
        featureDesc: {
            fontSize: 14,
            color: '#6b7280',
            textAlign: 'center',
        },
        servicesSection: {
            paddingVertical: 60,
            paddingHorizontal: 20,
            backgroundColor: '#fff',
        },
        servicesGrid: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 16,
            maxWidth: 900,
            alignSelf: 'center',
        },
        serviceItem: {
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: '#f8fafc',
            paddingVertical: 14,
            paddingHorizontal: 20,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#e5e7eb',
            gap: 12,
        },
        serviceItemText: {
            fontSize: 15,
            color: '#1f2937',
        },
    });

    return (
        <View style={styles.container}>
            <Header />
            <ScrollView style={styles.scrollView}>
                <View style={styles.hero}>
                    <View style={styles.heroContent}>
                        <View style={styles.heroLeft}>
                            <Text style={styles.heroLabel}>Expert Tax Filing</Text>
                            <Text style={styles.heroTitle}>Income Tax Return Filing</Text>
                            <Text style={styles.heroSubtitle}>
                                File your Income Tax Returns accurately with expert guidance. We maximize your tax benefits while ensuring 100% compliance with IT department regulations.
                            </Text>
                        </View>
                        <View style={styles.heroRight}>
                            {!submitted ? (
                                <View style={styles.formCard}>
                                    <Text style={styles.formTitle}>Get a Free Quote</Text>
                                    <Text style={styles.formSubtitle}>Fill the form and our team will connect with you</Text>

                                    {error ? <Text style={styles.errorText}>{error}</Text> : null}

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>Full Name <Text style={styles.required}>*</Text></Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="Enter your name"
                                            placeholderTextColor="#9ca3af"
                                            value={formData.name}
                                            onChangeText={(text) => setFormData({ ...formData, name: text })}
                                        />
                                    </View>

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>Email Address <Text style={styles.required}>*</Text></Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="Enter your email"
                                            placeholderTextColor="#9ca3af"
                                            keyboardType="email-address"
                                            value={formData.email}
                                            onChangeText={(text) => setFormData({ ...formData, email: text })}
                                        />
                                    </View>

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>Phone Number <Text style={styles.required}>*</Text></Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="Enter your phone"
                                            placeholderTextColor="#9ca3af"
                                            keyboardType="phone-pad"
                                            value={formData.phone}
                                            onChangeText={(text) => setFormData({ ...formData, phone: text })}
                                        />
                                    </View>

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>ITR Type</Text>
                                        <View style={styles.typeGrid}>
                                            {itrTypes.map((type) => (
                                                <TouchableOpacity
                                                    key={type.id}
                                                    style={[styles.typeButton, formData.itrType === type.id && styles.typeButtonSelected]}
                                                    onPress={() => setFormData({ ...formData, itrType: type.id })}
                                                >
                                                    <Text style={[styles.typeButtonText, formData.itrType === type.id && styles.typeButtonTextSelected]}>
                                                        {type.label}
                                                    </Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                    </View>

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>Primary Income Source</Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="e.g., Salary, Business, Freelance"
                                            placeholderTextColor="#9ca3af"
                                            value={formData.incomeSource}
                                            onChangeText={(text) => setFormData({ ...formData, incomeSource: text })}
                                        />
                                    </View>

                                    <TouchableOpacity
                                        style={styles.submitButton}
                                        onPress={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <ActivityIndicator color="#1a1a4e" />
                                        ) : (
                                            <Text style={styles.submitButtonText}>Submit Request</Text>
                                        )}
                                    </TouchableOpacity>
                                </View>
                            ) : (
                                <View style={styles.successCard}>
                                    <View style={styles.successIcon}>
                                        <Ionicons name="checkmark" size={40} color="#fff" />
                                    </View>
                                    <Text style={styles.successTitle}>Request Submitted!</Text>
                                    <Text style={styles.successText}>
                                        Thank you for your inquiry. We've sent a confirmation email to {formData.email}. Our team will connect with you within 24-48 hours.
                                    </Text>
                                </View>
                            )}
                        </View>
                    </View>
                </View>

                <View style={styles.featuresSection}>
                    <View style={styles.featuresContainer}>
                        <Text style={styles.sectionTitle}>Why Choose Our ITR Services?</Text>
                        <View style={styles.featuresGrid}>
                            {features.map((feature, index) => (
                                <View key={index} style={styles.featureCard}>
                                    <View style={styles.featureIcon}>
                                        <Ionicons name={feature.icon} size={26} color="#c9a44e" />
                                    </View>
                                    <Text style={styles.featureTitle}>{feature.title}</Text>
                                    <Text style={styles.featureDesc}>{feature.desc}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                </View>

                <View style={styles.servicesSection}>
                    <View style={styles.featuresContainer}>
                        <Text style={styles.sectionTitle}>Our ITR Services Include</Text>
                        <View style={styles.servicesGrid}>
                            {['Salaried ITR Filing', 'Business ITR Filing', 'Freelancer Returns', 'Capital Gains Filing', 'NRI Tax Filing', 'Revised Returns', 'Tax Planning', 'Refund Assistance'].map((service, index) => (
                                <View key={index} style={styles.serviceItem}>
                                    <Ionicons name="checkmark-circle" size={20} color="#10b981" />
                                    <Text style={styles.serviceItemText}>{service}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                </View>

                {/* ITR Types Section */}
                <View style={[styles.servicesSection, { backgroundColor: '#f8fafc' }]}>
                    <View style={styles.featuresContainer}>
                        <Text style={styles.sectionTitle}>Which ITR Form Do You Need?</Text>
                        <Text style={{ fontSize: 15, color: '#6b7280', textAlign: 'center', marginBottom: 40, maxWidth: 700, alignSelf: 'center' }}>
                            Choosing the right ITR form is crucial for accurate filing. Our experts will help you select the appropriate form based on your income sources.
                        </Text>
                        <View style={{ flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: 20, justifyContent: 'center' }}>
                            {[
                                { form: 'ITR-1 (Sahaj)', desc: 'For salaried individuals with income up to ₹50 lakhs from salary, one house property, and other sources', suitable: 'Suitable for most salaried employees' },
                                { form: 'ITR-2', desc: 'For individuals having capital gains, more than one house property, or foreign assets', suitable: 'For investors and property owners' },
                                { form: 'ITR-3', desc: 'For individuals having income from business or profession', suitable: 'For business owners and professionals' },
                                { form: 'ITR-4 (Sugam)', desc: 'For presumptive income under Section 44AD, 44ADA, or 44AE', suitable: 'For small businesses and freelancers' },
                            ].map((item, index) => (
                                <View key={index} style={{ backgroundColor: '#fff', padding: 24, borderRadius: 12, borderWidth: 1, borderColor: '#e5e7eb', width: isMobile ? '100%' : '48%', minWidth: 280 }}>
                                    <Text style={{ fontSize: 18, fontWeight: '700', color: '#1a1a4e', marginBottom: 12 }}>{item.form}</Text>
                                    <Text style={{ fontSize: 14, color: '#374151', marginBottom: 12, lineHeight: 22 }}>{item.desc}</Text>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        <Ionicons name="person" size={16} color="#c9a44e" />
                                        <Text style={{ fontSize: 13, color: '#c9a44e', fontWeight: '600' }}>{item.suitable}</Text>
                                    </View>
                                </View>
                            ))}
                        </View>
                    </View>
                </View>

                {/* Documents Required Section */}
                <View style={styles.servicesSection}>
                    <View style={styles.featuresContainer}>
                        <Text style={styles.sectionTitle}>Documents Required for ITR Filing</Text>
                        <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 32, maxWidth: 900, alignSelf: 'center' }}>
                            <View style={{ flex: 1 }}>
                                <Text style={{ fontSize: 18, fontWeight: '700', color: '#1a1a4e', marginBottom: 16 }}>Identity & Income Proof</Text>
                                {['PAN Card', 'Aadhaar Card', 'Form 16 from employer', 'Form 26AS (Tax Credit Statement)', 'AIS (Annual Information Statement)', 'Salary slips (if Form 16 not available)'].map((doc, i) => (
                                    <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12, gap: 10 }}>
                                        <Ionicons name="document-text" size={18} color="#c9a44e" style={{ marginTop: 2 }} />
                                        <Text style={{ fontSize: 15, color: '#374151', flex: 1 }}>{doc}</Text>
                                    </View>
                                ))}
                            </View>
                            <View style={{ flex: 1 }}>
                                <Text style={{ fontSize: 18, fontWeight: '700', color: '#1a1a4e', marginBottom: 16 }}>Investment & Deduction Proofs</Text>
                                {['80C investment proofs (PPF, ELSS, LIC)', 'Health insurance premium receipts (80D)', 'Home loan interest certificate', 'Rent receipts for HRA claims', 'Bank statements for interest income', 'Capital gains statements'].map((doc, i) => (
                                    <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12, gap: 10 }}>
                                        <Ionicons name="document-text" size={18} color="#c9a44e" style={{ marginTop: 2 }} />
                                        <Text style={{ fontSize: 15, color: '#374151', flex: 1 }}>{doc}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                </View>

                {/* ITR FAQ Section */}
                <View style={[styles.servicesSection, { backgroundColor: '#f8fafc' }]}>
                    <View style={styles.featuresContainer}>
                        <Text style={styles.sectionTitle}>Frequently Asked Questions</Text>
                        {[
                            { q: 'What is the deadline for filing ITR?', a: 'The deadline is usually July 31st for individuals and non-audit cases. For audit cases, it is October 31st. Late filing attracts a penalty of up to ₹5,000.' },
                            { q: 'How long does refund processing take?', a: 'Tax refunds are typically processed within 4-6 weeks of filing and verification. E-verified returns are processed faster than those verified by post.' },
                            { q: 'Can I revise my ITR after filing?', a: 'Yes, you can file a revised return within 3 months before the end of the relevant assessment year. We help ensure your original filing is accurate to avoid revisions.' },
                            { q: 'What happens if I miss the deadline?', a: 'Late filing attracts a penalty of ₹5,000 (reduced to ₹1,000 if income is below ₹5 lakhs). You also lose the ability to carry forward certain losses.' },
                        ].map((faq, index) => (
                            <View key={index} style={{ backgroundColor: '#fff', padding: 20, borderRadius: 10, marginBottom: 16, borderWidth: 1, borderColor: '#e5e7eb' }}>
                                <Text style={{ fontSize: 16, fontWeight: '700', color: '#1a1a4e', marginBottom: 10 }}>{faq.q}</Text>
                                <Text style={{ fontSize: 15, color: '#6b7280', lineHeight: 24 }}>{faq.a}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                {/* CTA Section */}
                <View style={{ backgroundColor: '#1a1a4e', paddingVertical: 60, paddingHorizontal: 20 }}>
                    <View style={{ maxWidth: 700, alignSelf: 'center', alignItems: 'center' }}>
                        <Text style={{ fontSize: isMobile ? 24 : 32, fontWeight: '800', color: '#c9a44e', textAlign: 'center', marginBottom: 16 }}>
                            File Your ITR With Confidence
                        </Text>
                        <Text style={{ fontSize: 16, color: 'rgba(255,255,255,0.8)', textAlign: 'center', marginBottom: 30 }}>
                            Don't miss the deadline. Let our tax experts handle your ITR filing and maximize your refunds legally.
                        </Text>
                        <TouchableOpacity
                            style={{ backgroundColor: '#c9a44e', paddingVertical: 16, paddingHorizontal: 40, borderRadius: 8 }}
                            onPress={() => { }}
                        >
                            <Text style={{ color: '#1a1a4e', fontWeight: '700', fontSize: 16 }}>Get Started Now</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                <Footer />
            </ScrollView>
        </View>
    );
};

export default ITRFilingScreen;
